// apps/curbpass/src/routes/vehicles/components/VehiclesTable.tsx
import React, { useEffect, useMemo, useCallback } from "react";
import debounce from "lodash/debounce";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import Fab from "@mui/material/Fab";
import { DataGridPro, GridSelectionModel, useGridApiRef } from "@mui/x-data-grid-pro";
import { VehicleType, VehicleTypeValue } from "@app.automotus.io/components/common";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { VehicleMobileList } from "./VehicleMobileList";
import { VehicleMobileDrawer } from "./VehicleMobileDrawer";
import { RegisteredVehicle } from "common/graphql";
import { EditBuffer, NewVehicleRow, VehicleTableRow } from "../vehicles.types";
import { validateVehicleRow } from "../validateVehicleRow";
import { useVehicleEditMode } from "../hooks/useVehicleEditMode";
import { useVehicleColumns } from "../hooks/useVehicleColumns";
import { VehiclesTableToolbar } from "./VehiclesTableToolbar";
import { DeleteVehicleConfirmDialog } from "./DeleteVehicleConfirmDialog";

interface VehiclesTableProps {
  existingVehicles: RegisteredVehicle[];
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSearchChange: (value: string) => void;
  newRows: NewVehicleRow[];
  onNewRowsChange: (rows: NewVehicleRow[]) => void;
  onDeleteExisting: (ids: string[]) => Promise<void>;
  onSaveEdit: (id: string, data: EditBuffer) => Promise<void>;
  onSubmitNew: (
    data: Pick<NewVehicleRow, "stateCode" | "licensePlate" | "make" | "model" | "type" | "category">,
  ) => Promise<void>;
  loading?: boolean;
  disabled?: boolean;
  footerActions?: React.ReactNode;
}

const NoVehiclesOverlay = () => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
    <Typography color="text.secondary">No vehicles yet.</Typography>
  </Box>
);

export const VehiclesTable: React.FC<VehiclesTableProps> = ({
  existingVehicles,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onSearchChange,
  newRows,
  onNewRowsChange,
  onDeleteExisting,
  onSaveEdit,
  onSubmitNew,
  loading,
  disabled,
  footerActions,
}) => {
  const apiRef = useGridApiRef();
  const [searchInput, setSearchInput] = React.useState("");
  const [rowSelectionModel, setRowSelectionModel] = React.useState<GridSelectionModel>([]);

  // Stable ref so the debounced function doesn't need to be recreated when onSearchChange changes
  const onSearchChangeRef = React.useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;

  const debouncedOnSearchChange = useMemo(() => debounce((value: string) => onSearchChangeRef.current(value), 300), []);

  // Cancel any deferred call on unmount: a debounced search scheduled while the
  // user was typing would otherwise fire after navigation, driving a wasted
  // refetch through the parent's onSearchChange (AUT-5346).
  useEffect(() => () => debouncedOnSearchChange.cancel(), [debouncedOnSearchChange]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      debouncedOnSearchChange(value);
    },
    [debouncedOnSearchChange],
  );

  // Mobile drawer state
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);
  const [mobileDrawerVehicle, setMobileDrawerVehicle] = React.useState<VehicleTableRow | null>(null);

  const handleMobileRowClick = useCallback((row: VehicleTableRow) => {
    setMobileDrawerVehicle(row);
    setMobileDrawerOpen(true);
  }, []);

  const handleMobileAddNew = useCallback(() => {
    setMobileDrawerVehicle(null);
    setMobileDrawerOpen(true);
  }, []);

  const handleMobileDrawerClose = useCallback(() => {
    setMobileDrawerOpen(false);
  }, []);

  const handleMobileDeleteExisting = useCallback(
    async (id: string) => {
      await onDeleteExisting([id]);
    },
    [onDeleteExisting],
  );

  // Delete confirmation state
  const [confirmDeleteId, setConfirmDeleteId] = React.useState<string | null>(null);
  const [isDeletingExisting, setIsDeletingExisting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  // Edit mode
  const {
    editingRowId,
    editBuffer,
    editErrors,
    isSaving,
    saveError,
    handleStartEdit,
    handleEditBufferChange,
    handleSave,
    handleCancelEdit,
  } = useVehicleEditMode({ existingVehicles, newRows, onSaveEdit });

  const updateNewRow = useCallback(
    (id: string, patch: Partial<NewVehicleRow>) => {
      onNewRowsChange(
        newRows.map((row) => {
          if (row.id !== id) {
            return row;
          }
          const updated = { ...row, ...patch };
          const otherNewRows = newRows.filter((r) => r.id !== id);
          const { errors } = validateVehicleRow(updated, otherNewRows, existingVehicles);
          return { ...updated, errors, submitErrorCode: undefined };
        }),
      );
    },
    [newRows, existingVehicles, onNewRowsChange],
  );

  const handleDeleteRow = useCallback(
    (id: string) => {
      if (newRows.find((r) => r.id === id)) {
        // New (pending) rows: remove immediately without confirmation
        onNewRowsChange(newRows.filter((r) => r.id !== id));
      } else {
        // Existing vehicles: show confirmation dialog
        setConfirmDeleteId(id);
      }
    },
    [newRows, onNewRowsChange],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!confirmDeleteId) {
      return;
    }
    setIsDeletingExisting(true);
    try {
      await onDeleteExisting([confirmDeleteId]);
      setConfirmDeleteId(null);
      setDeleteError(null);
    } catch {
      setDeleteError("Failed to remove vehicle. Please try again.");
    } finally {
      setIsDeletingExisting(false);
    }
  }, [confirmDeleteId, onDeleteExisting]);

  const handleDeleteSelected = useCallback(async () => {
    const selectedIds = rowSelectionModel as string[];
    const existingIds = selectedIds.filter((id) => !newRows.find((r) => r.id === id));
    const newIds = selectedIds.filter((id) => newRows.find((r) => r.id === id));

    if (existingIds.length > 0) {
      setIsDeletingExisting(true);
      try {
        await onDeleteExisting(existingIds);
      } catch (err) {
        setIsDeletingExisting(false);
        throw err;
      }
      setIsDeletingExisting(false);
    }

    if (newIds.length > 0) {
      onNewRowsChange(newRows.filter((r) => !newIds.includes(r.id)));
    }
    setRowSelectionModel([]);
  }, [rowSelectionModel, newRows, onDeleteExisting, onNewRowsChange]);

  const handleAddRow = useCallback(() => {
    const newRow: NewVehicleRow = {
      id: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      stateCode: "",
      licensePlate: "",
      make: "",
      model: "",
      type: "",
      category: "commercial",
      errors: {
        stateCode: "State is required",
        licensePlate: "License plate is required",
        type: "Vehicle type is required",
      },
    };
    onNewRowsChange([newRow, ...newRows]);
    setTimeout(() => {
      apiRef.current.scrollToIndexes({ rowIndex: 0 });
    }, 50);
  }, [newRows, onNewRowsChange, apiRef]);

  // --- Row data ---
  // existingVehicles are pre-filtered server-side; only pending rows need client-side filtering

  const rows: VehicleTableRow[] = useMemo(() => {
    const search = searchInput.toLowerCase();
    const existing: VehicleTableRow[] = existingVehicles.map((v) => ({
      id: v.id,
      stateCode: v.stateCode,
      licensePlate: v.licensePlate,
      make: v.make,
      model: v.model,
      type: v.type,
      category: v.category,
      isNew: false,
    }));

    const pending: VehicleTableRow[] = newRows
      .map((r) => ({ ...r, isNew: true }))
      .filter(
        (r) =>
          !search ||
          r.licensePlate.toLowerCase().includes(search) ||
          (r.make ?? "").toLowerCase().includes(search) ||
          (r.model ?? "").toLowerCase().includes(search) ||
          r.stateCode.toLowerCase().includes(search) ||
          (r.category ?? "").toLowerCase().includes(search) ||
          (VehicleType.FromValue(r.type as VehicleTypeValue)?.label ?? r.type ?? "").toLowerCase().includes(search),
      );
    return [...pending, ...existing];
  }, [existingVehicles, newRows, searchInput]);

  // --- Columns ---

  const columns = useVehicleColumns({
    editingRowId,
    editBuffer,
    editErrors,
    isSaving,
    disabled,
    updateNewRow,
    handleEditBufferChange,
    handleStartEdit,
    handleSave,
    handleCancelEdit,
    handleDeleteRow,
  });

  const mobileRows = useMemo(() => rows.filter((r) => !r.isNew), [rows]);

  const handleDeleteNewRow = useCallback(
    (id: string) => onNewRowsChange(newRows.filter((r) => r.id !== id)),
    [newRows, onNewRowsChange],
  );

  if (isMobile) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        {/* Mobile toolbar */}
        <VehiclesTableToolbar
          searchValue={searchInput}
          onSearchChange={handleSearchChange}
          selectedCount={0}
          onDeleteSelected={() => Promise.resolve()}
        />

        {/* Scrollable card list — pb clears the FAB */}
        <Box sx={{ flex: 1, overflowY: "auto", bgcolor: "background.paper", pb: "88px" }}>
          {loading && mobileRows.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <VehicleMobileList
              rows={mobileRows}
              onRowClick={handleMobileRowClick}
              onDeleteNewRow={handleDeleteNewRow}
            />
          )}
        </Box>

        {/* FAB */}
        <Fab
          color="primary"
          onClick={handleMobileAddNew}
          disabled={disabled}
          aria-label="Add vehicle"
          sx={{ position: "fixed", bottom: 24, right: 24 }}
        >
          <AddIcon />
        </Fab>

        {/* Drawer */}
        <VehicleMobileDrawer
          open={mobileDrawerOpen}
          vehicle={mobileDrawerVehicle}
          onClose={handleMobileDrawerClose}
          onSaveEdit={onSaveEdit}
          onSubmitNew={onSubmitNew}
          onDeleteExisting={handleMobileDeleteExisting}
          existingVehicles={existingVehicles}
          newRows={newRows}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <VehiclesTableToolbar
        searchValue={searchInput}
        onSearchChange={handleSearchChange}
        selectedCount={rowSelectionModel.length}
        onDeleteSelected={handleDeleteSelected}
        onAddRow={handleAddRow}
        disabled={disabled}
        isDeleting={isDeletingExisting}
      />
      <DataGridPro
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        loading={loading}
        checkboxSelection
        selectionModel={rowSelectionModel}
        onSelectionModelChange={setRowSelectionModel}
        disableSelectionOnClick
        getRowClassName={(params) => {
          const row = params.row as VehicleTableRow;
          if (row.id === editingRowId) {
            return "vehicle-row--editing";
          }
          if (row.isNew) {
            return Object.keys(row.errors ?? {}).length > 0 || !!row.submitErrorCode
              ? "vehicle-row--error"
              : "vehicle-row--new";
          }
          return "";
        }}
        sx={{
          "& .vehicle-row--new": { backgroundColor: "#f0faf0" },
          "& .vehicle-row--error": { backgroundColor: "#fde8e8" },
          "& .vehicle-row--editing": { backgroundColor: "#f5f9ff" },
          flex: 1,
          minHeight: 0,
        }}
        pagination
        paginationMode="server"
        rowCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={(newSize) => {
          onPageSizeChange(newSize);
          onPageChange(0);
        }}
        rowsPerPageOptions={[50, 100]}
        components={{ NoRowsOverlay: loading ? () => null : NoVehiclesOverlay }}
      />
      {saveError && (
        <Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.5, display: "block" }}>
          {saveError}
        </Typography>
      )}
      {footerActions && (
        <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mt: 1 }}>
          {footerActions}
        </Stack>
      )}

      {/* Delete confirmation dialog */}
      <DeleteVehicleConfirmDialog
        open={confirmDeleteId !== null}
        isDeleting={isDeletingExisting}
        errorMessage={deleteError}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setConfirmDeleteId(null);
          setDeleteError(null);
        }}
      />
    </Box>
  );
};

export default VehiclesTable;
